/**
 * ProjectService — the WORK binding of the projects layer (`projects`, Phase 2
 * unified task/ops). teacherTasks.projectId points here. Progress is ALWAYS
 * computed (completed ÷ total tasks) at read time, never stored.
 *
 * The implementation moved to projectServiceFactory.js when Projects gained a
 * second space (Personal → `personalProjects`). This file is a pure move: the
 * public API below is byte-for-byte what every existing caller already imported.
 *
 * WORK IS BOUND READ-ONLY, and that is a decision rather than an oversight.
 * `projects` is admin-write in firestore.rules and readable by the whole team,
 * and no create path has ever existed for it — the five live studio projects
 * were Notion-imported through the Admin SDK, which bypasses rules. Giving Work
 * project creation is a fine thing to want and a separate change with its own
 * decision behind it; it must not arrive as a side effect of making Personal
 * work. `writable: false` is what enforces that: the writers are not merely
 * unused here, they are not on the object.
 *
 * Live projects: Brand & Marketing, Recital, Summer 2026, Systems Cleanup,
 * Website/Portal Build.
 */

import { createProjectService } from './projectServiceFactory';

export const workProjectService = createProjectService({
	projectsCollection: 'projects',
	space: 'work',
	writable: false,
});

/**
 * Subscribe to all projects (real-time).
 * @param {Function} callback - receives an array of project docs
 * @returns {Function} unsubscribe
 */
export const subscribeToProjects = workProjectService.subscribeToProjects;

/**
 * Compute a project's progress from its tasks. Sub-tasks (parentId set) count
 * the same as top-level tasks — every task that belongs to the project.
 * @param {Array} tasks - all tasks (will be filtered to this project)
 * @param {string} projectId
 * @returns {{ done:number, total:number, pct:number, inProgress:number, todo:number }}
 */
export const computeProjectProgress = (tasks, projectId) => {
	const mine = (tasks || []).filter((t) => t.projectId === projectId);
	const total = mine.length;
	const done = mine.filter((t) => t.status === 'completed').length;
	const inProgress = mine.filter((t) => t.status === 'in_progress').length;
	const todo = mine.filter((t) => t.status === 'pending').length;
	return { done, total, todo, inProgress, pct: total ? Math.round((done / total) * 100) : 0 };
};

const ProjectService = { subscribeToProjects, computeProjectProgress };
export default ProjectService;
